// ============================================================================
// LES INTÉGRATIONS EXTERNES — une carte par OUTIL, et son état réel.
//
// POURQUOI CET ÉCRAN A ÉTÉ REFAIT (2026-09-01, « c'est complètement brouillon et on comprend rien ») :
// la page rangeait ses blocs par LIEU DE STOCKAGE — les réglages de la Console d'un côté, ceux du
// back-office de l'autre — alors qu'on y vient toujours avec un OUTIL en tête (« je veux brancher les
// SMS »). Deux blocs s'appelaient presque pareil, séparés par un troisième sans rapport ; rien ne
// disait ce qui était branché ; et l'avertissement « pas encore utilisé » se répétait à chaque ligne
// jusqu'à ne plus se voir. Le lieu de stockage reste une vérité importante — il devient une PROPRIÉTÉ
// de la carte, plus le plan de la page.
//
// ⚠️ L'ÉTAT EST DÉRIVÉ, JAMAIS AFFIRMÉ. « Configuré » se lit dans les réglages ; « éprouvé » ne
//    s'obtient qu'en appelant vraiment le fournisseur. Les confondre ferait dire à l'écran qu'un canal
//    marche parce qu'un champ est rempli — c'est exactement le mensonge de §23.0.
//
// ⚠️ L'ÉPREUVE N'EST PAS CONSERVÉE : elle vaut pour la session en cours, et l'écran le DIT. Un badge
//    vert qui survivrait au rechargement affirmerait un fait qu'on n'a pas revérifié.
// ============================================================================
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { alert, Icon, showToast, type IconName } from '@orgazone/ui-web';
import { api, type BoPlatformSetting } from '../api';
import { Pill, type Tone } from '../ui';

// ── L'ÉTAT D'UNE INTÉGRATION ──────────────────────────────────────────────────────────────────────
// Six états, parce qu’il y a six situations qui demandent six gestes différents. Les réduire à
// « ok / pas ok » obligerait à ouvrir chaque carte pour savoir quoi faire.
export type EtatIntegration =
  | { k: 'absent' }
  | { k: 'incomplet'; manque: string }
  /**
   * Réglé, mais AUCUN code ne le consomme encore.
   * ⚠️ Distinct de « configuré », et c'est le rendu qui l'a montré : le portefeuille s'affichait
   * configuré alors que rien ne l'appelle. La carte portait bien l'avertissement, mais la pastille et
   * la synthèse disaient l'inverse — et c'est la pastille qu'on lit en diagonale.
   */
  | { k: 'inerte' }
  | { k: 'configure' }
  | { k: 'eprouve'; a: string }
  | { k: 'echec'; motif: string };

const ETAT_RENDU: Record<EtatIntegration['k'], { tone: Tone; label: string }> = {
  absent: { tone: 'ne', label: 'non configuré' },
  incomplet: { tone: 'wa', label: 'incomplet' },
  // ⚠️ Le mot dit la vérité entière : la valeur est là, personne ne la lit. Ce n'est pas une faute de
  //    l'exploitant — c'est un état de NOTRE logiciel, d'où le ton neutre plutôt qu'un avertissement.
  inerte: { tone: 'ne', label: 'déclaré · pas encore consommé' },
  // ⚠️ « configuré » et non « prêt » : des champs remplis ne prouvent rien tant que le fournisseur
  //    n'a pas répondu. Le mot doit rester en deçà de ce qu'on sait.
  configure: { tone: 'in', label: 'configuré · non éprouvé' },
  eprouve: { tone: 'ok', label: 'éprouvé' },
  echec: { tone: 'da', label: 'en échec' },
};

export function PastilleEtat({ etat }: { etat: EtatIntegration }) {
  const r = ETAT_RENDU[etat.k];
  return (
    <Pill tone={r.tone} shape="tight">
      {etat.k === 'eprouve' ? `${r.label} à ${etat.a}` : r.label}
    </Pill>
  );
}

// ── CE QU'UNE CARTE RAPPORTE À LA PAGE ─────────────────────────────────────────────────────────────
// ⚠️ LA CARTE EST LA SOURCE DE SON ÉTAT, et la vue d'ensemble n'en recalcule aucun : la première version les dérivait
//    des deux côtés, et le rendu montrait une carte « inerte » comptée « configurée » dix centimètres au-dessus
//    (Forme A). Elle rapporte aussi ses MOTS (nom, rôle, stockage) : la vue d'ensemble les reprend tels quels au lieu
//    d'en écrire une seconde version.

/** Les onglets de la page Intégrations. */
export type IntegrationTab = 'all' | 'mail' | 'sms' | 'wallet';

/** Ce qu'une carte dit d'elle-même à la page. */
export interface IntegrationReport {
  key: string;
  tab: IntegrationTab;
  name: string;
  provider: string;
  etat: EtatIntegration;
  role: ReactNode;
  storage: string;
}

/** L'onglet où la carte est posée, et à qui elle rapporte. Hors de la page (une carte seule), elle ne rapporte rien. */
export const IntegrationReportContext = createContext<{ tab: IntegrationTab; report: (r: IntegrationReport) => void } | null>(null);

/**
 * Rapporte la carte à la page.
 * ⚠️ Le rôle est un NŒUD, recréé à chaque rendu : il ne décide donc pas du rapport, sans quoi chaque rendu reposerait
 *    un état, qui re-rendrait la page, qui reposerait un état… Ce qui décide, c'est ce qui se compare.
 */
export function useReportIntegration(r: Omit<IntegrationReport, 'tab'>): void {
  const ctx = useContext(IntegrationReportContext);
  const latest = useRef(r);
  latest.current = r;
  const signature = JSON.stringify([r.key, r.name, r.provider, r.etat, r.storage]);
  useEffect(() => { if (ctx) ctx.report({ ...latest.current, tab: ctx.tab }); }, [ctx, signature]);
}

// ── LE GABARIT D'UNE CARTE ────────────────────────────────────────────────────────────────────────
// La MÊME géométrie que `settings-mail-form` (bandeau `--head`, corps en grille) : quatre intégrations
// qui se ressemblent se comparent d'un coup d'œil, quatre qui divergent se relisent une par une.

/** L'ossature commune : identité, état, périmètre, corps, et le pied qui dit où ça vit. */
export function CarteIntegration({ icone, heading, fournisseur, etat, role, stockage, avertissement, action, children }: {
  icone: IconName;
  heading: string;
  /** Le nom du service tiers — celui qu'on cherche dans une facture ou une doc. */
  fournisseur: string;
  etat: EtatIntegration;
  /** À quoi ça sert, et surtout CE QUI NE MARCHE PAS sans elle. */
  role: ReactNode;
  /** Où vit ce réglage. ⚠️ Conservé : une base magasin appartient au commerçant, jamais à nous. */
  stockage: string;
  /** Ce qui est enregistré mais pas encore consommé — UNE fois par carte, pas à chaque ligne. */
  avertissement?: ReactNode;
  /** L'action principale (éprouver, le plus souvent) — dans le bandeau, à droite du titre. */
  action?: ReactNode;
  children: ReactNode;
}) {
  useReportIntegration({ key: `${heading}|${fournisseur}`, name: heading, provider: fournisseur, etat, role, storage: stockage });
  return (
    <div className="dcard" style={{ overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '14px 18px', background: 'var(--head)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Icon name={icone} size={18} />
          <div style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>{heading}</div>
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{fournisseur}</span>
          <PastilleEtat etat={etat} />
          <span style={{ flex: 1 }} />
          {action}
        </div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3, maxWidth: 640, lineHeight: 1.5 }}>
          {role}
        </div>
        {/* ⚠️ Le motif d'un état négatif se lit SUR la carte, pas après un clic : « incomplet » sans
            dire quoi oblige à ouvrir chaque champ pour trouver lequel manque. */}
        {etat.k === 'incomplet' && (
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--wa-fg)', marginTop: 6, lineHeight: 1.5 }}>
            Il manque : {etat.manque}
          </div>
        )}
        {etat.k === 'echec' && (
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--da-fg)', marginTop: 6, lineHeight: 1.5 }}>
            {etat.motif}
          </div>
        )}
      </div>

      {avertissement && (
        <div style={{
          fontSize: 'var(--t-note)', color: 'var(--wa-fg)', background: 'var(--wa-bg)',
          padding: '10px 18px', lineHeight: 1.5,
        }}>
          ⚠️ <b>Enregistré, mais pas encore utilisé.</b> {avertissement}
        </div>
      )}

      {children}

      <div style={{
        padding: '9px 18px', borderTop: '1px solid var(--ink200)',
        fontSize: 'var(--t-meta)', color: 'var(--ink500)', display: 'flex', gap: 6, alignItems: 'center',
      }}>
        <Icon name="lock" size={12} />
        {stockage}
      </div>
    </div>
  );
}

// ── UNE LIGNE DE RÉGLAGE ──────────────────────────────────────────────────────────────────────────

/**
 * Un réglage du back-office : sa valeur publique, son éventuel secret, et QUI l'a changé en dernier.
 * ⚠️ L'avertissement « pas encore utilisé » a QUITTÉ cette ligne pour monter sur la carte : répété sur
 * chaque réglage d'une même famille, il apprenait à ne plus le lire.
 */
export function LigneReglage({ s, onSave }: {
  s: BoPlatformSetting;
  onSave: (v: { value?: string | null; secret?: string }) => Promise<void>;
}) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [value, setValue] = useState(s.value ?? '');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);

  // Le parent recharge après écriture : la ligne repart de ce que le serveur dit, pas de ce qu'on a tapé.
  useEffect(() => { setValue(s.value ?? ''); setSecret(''); }, [s]);

  // ⚠️ RIEN CHANGÉ = BOUTON INERTE. Une écriture à vide réécrirait « modifié le … par … » alors que
  //    personne n'a rien modifié : la trace d'audit désignerait un innocent le jour où les envois
  //    s'arrêtent. C'est cette trace qu'on protège, pas un clic de trop.
  const modifie = value !== (s.value ?? '') || secret !== '';

  const save = async () => {
    setBusy(true);
    try {
      await onSave({ value: value.trim() || null, ...(secret ? { secret } : {}) });
      showToast('Réglage enregistré.');
    } catch (e) { void alert({ title: 'Réglage refusé', message: (e as Error).message }); }
    finally { setBusy(false); }
  };

  const lbl: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink500)', marginBottom: 5 };

  return (
    <div style={{ padding: '13px 18px', borderTop: '1px solid var(--ink200)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{s.note}</span>
        {/* La clé technique reste visible : c'est elle qu'on cite dans un message d'erreur serveur. */}
        <span className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{s.key}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: s.hasSecret ? 'repeat(auto-fit,minmax(210px,1fr))' : '1fr', gap: '10px 16px' }}>
        <div style={{ minWidth: 0 }}>
          <div id={`${fieldId}-1`} style={lbl}>{s.hasSecret ? 'Identifiant' : 'Valeur'}</div>
          {/* ⚠️ VALEURS FINIES = CHOIX, jamais champ libre : demander à l'opérateur de deviner ce que
              l'API accepte, c'est garantir qu'il se trompera un jour — et l'erreur ne se verrait qu'à
              l'appel suivant, ailleurs. « Par défaut » est une option à part entière : le vide n'est
              pas neutre (Eldowallet répond alors en anglais), il doit donc se choisir sciemment. */}
          {s.choices ? (
            <div className="seg-track">
              {[{ value: '', label: 'Par défaut' }, ...s.choices].map((c) => (
                <button aria-pressed={value === c.value} key={c.value} type="button" className={value === c.value ? 'seg active' : 'seg'}
                  onClick={() => setValue(c.value)}>{c.label}</button>
              ))}
            </div>
          ) : (
            <input aria-labelledby={`${fieldId}-1`} className="field-input mono" value={value} onChange={(e) => setValue(e.target.value)}
              placeholder={s.hasSecret ? 'compte@exemple.fr' : 'non réglé'} />
          )}
        </div>
        {s.hasSecret && (
          <div style={{ minWidth: 0 }}>
            <div id={`${fieldId}-0`} style={lbl}>Secret</div>
            <input aria-labelledby={`${fieldId}-0`} className="field-input mono" type="password" value={secret} autoComplete="new-password"
              onChange={(e) => setSecret(e.target.value)}
              placeholder={s.secretSetAt ? '•••••••• (inchangé)' : 'à renseigner'} />
            {/* Le secret ne redescend jamais du serveur : on ne peut que le REMPLACER, jamais le relire. */}
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.45 }}>
              {s.secretSetAt
                ? `Défini le ${new Date(s.secretSetAt).toLocaleDateString('fr-FR')} — laisser vide pour le conserver.`
                : 'Jamais défini.'}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-cta sm" style={{ width: 'auto' }} disabled={busy || !modifie} onClick={() => void save()}>
          {busy ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        {/* Qui a changé ce compte fournisseur, et quand — la question qu'on pose le jour de la panne. */}
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.45 }}>
          {s.updatedAt
            ? `Modifié le ${new Date(s.updatedAt).toLocaleString('fr-FR')}${s.updatedBy ? ` par ${s.updatedBy}` : ''}.`
            : 'Jamais réglé.'}
        </span>
      </div>
    </div>
  );
}

// ── SMS ENVOI ─────────────────────────────────────────────────────────────────────────────────────

const heure = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

/**
 * L'intégration SMS Envoi — compte maître, dont dérivent les sous-comptes des magasins.
 *
 * ⚠️ DEUX VOIES D'ACCÈS, et l'écran doit dire laquelle a servi : le jeton d'API (aucun mot de passe
 *    stocké, voie préférée) ou les identifiants. Un jeton refusé se regénère chez le fournisseur, un
 *    mot de passe faux se ressaisit — deux pannes qui se ressemblent et ne se réparent pas au même
 *    endroit.
 */
export function CarteSmsEnvoi({ settings, onWrite }: {
  settings: BoPlatformSetting[];
  onWrite: (key: string, v: { value?: string | null; secret?: string }) => Promise<void>;
}) {
  const [encours, setEncours] = useState(false);
  const [res, setRes] = useState<Awaited<ReturnType<typeof api.testBoSmsEnvoi>> | null>(null);
  const [teste, setTeste] = useState<Date | null>(null);
  const [replieOuvert, setReplieOuvert] = useState(false);

  const par = (k: string) => settings.find((s) => s.key === k);
  const cleApi = par('smsenvoi.apikey');
  const jeton = par('smsenvoi.token');
  const compte = par('smsenvoi.account');
  const url = par('smsenvoi.base_url');

  const aCleApi = Boolean(cleApi?.value && cleApi.secretSetAt);
  const aJeton = Boolean(jeton?.value && jeton.secretSetAt);
  const aCompte = Boolean(compte?.value && compte.secretSetAt);

  // Les réglages, rangés par CAPACITÉ — c'est le partage que l'API impose, pas une mise en page.
  const dEnvoi = settings.filter((s) => s.key === 'smsenvoi.apikey');
  const dRevente = settings.filter((s) => s.key === 'smsenvoi.token' || s.key === 'smsenvoi.base_url');
  const repli = settings.filter((s) => s.key === 'smsenvoi.account');

  /**
   * L'état de la carte, dérivé des réglages puis corrigé par l'épreuve.
   * ⚠️ « INCOMPLET » PLUTÔT QUE « ÉCHEC » QUAND UNE SEULE VOIE MARCHE : un compte qui envoie sans
   * savoir revendre n'est pas en panne — il lui manque un accès, et l'écran doit dire lequel.
   */
  const etat: EtatIntegration = useMemo(() => {
    const p = res?.probe;
    if (p) {
      if (p.envoi.ok && p.revente.ok) return { k: 'eprouve', a: heure(teste ?? new Date()) };
      if (p.envoi.ok) return { k: 'incomplet', manque: 'l’accès REST v1.0 — les SMS partent, mais ni sous-comptes ni crédits' };
      if (p.revente.ok) return { k: 'incomplet', manque: 'la clé d’API HTTP v2 — la revente marche, mais aucun SMS ne peut partir' };
      return { k: 'echec', motif: p.envoi.error ?? p.revente.error ?? 'motif non rendu' };
    }
    if (res && !res.ok) return { k: 'echec', motif: res.error ?? 'motif non rendu' };
    const manques: string[] = [];
    if (!aCleApi) manques.push('la clé d’API HTTP v2 (envoi)');
    if (!aJeton && !aCompte) manques.push('un accès REST v1.0 (sous-comptes et crédits)');
    if (!url?.value) manques.push('l’adresse de l’API REST');
    if (manques.length === 3) return { k: 'absent' };
    if (manques.length) return { k: 'incomplet', manque: manques.join(', ') };
    return { k: 'configure' };
  }, [res, teste, aCleApi, aJeton, aCompte, url?.value]);

  // ⚠️ Le repli s'OUVRE TOUT SEUL quand aucun jeton n'est posé : c'est alors le compte qui porte la
  //    voie revente, et le replier reviendrait à cacher le seul réglage qui la fait marcher.
  useEffect(() => { if (!aJeton) setReplieOuvert(true); }, [aJeton]);

  const lancer = async () => {
    setEncours(true); setRes(null);
    try {
      setRes(await api.testBoSmsEnvoi());
      setTeste(new Date());
    } catch (e) {
      setRes({ ok: false, error: e instanceof Error ? e.message : String(e) });
      setTeste(new Date());
    } finally { setEncours(false); }
  };

  const p = res?.probe;
  const groupHeading: CSSProperties = {
    padding: '11px 18px', borderTop: '1px solid var(--ink200)', background: 'var(--ink-50)',
    fontSize: 'var(--t-note)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  };

  return (
    <CarteIntegration
      icone="phone" heading="SMS" fournisseur="SMS Envoi" etat={etat}
      stockage="Base interne du back-office — jamais dans une base magasin."
      role={<>
        Deux accès pour deux usages : la <b>clé d’API</b> fait <b>partir les SMS</b>, l’accès <b>REST</b>
        ouvre les <b>sous-comptes et les crédits</b> des magasins. Un compte peut n’avoir que le premier.
      </>}
      action={
        <button type="button" className="btn ghost" onClick={() => void lancer()} disabled={encours}>
          {encours ? 'Épreuve…' : 'Éprouver les accès'}
        </button>
      }
    >
      {/* ⚠️ LE RÉSULTAT EST DÉTAILLÉ PAR CAPACITÉ : une ligne unique ne dirait pas laquelle des deux
          manque, alors qu'elles se réparent à deux endroits différents. */}
      {res && !p && (
        <div style={{ padding: '12px 18px', background: 'var(--da-bg)', color: 'var(--da-fg)', fontSize: 'var(--t-note)', lineHeight: 1.55 }}>
          <b>Épreuve impossible</b> : {res.error ?? 'motif non rendu'}
        </div>
      )}
      {p && (
        <div style={{ padding: '12px 18px', display: 'grid', gap: 8, fontSize: 'var(--t-note)', lineHeight: 1.55 }}>
          <div style={{ color: p.envoi.ok ? 'var(--ok-fg)' : 'var(--da-fg)' }}>
            {/* ⚠️ « IDENTIFIANTS ACCEPTÉS », PAS « L'ENVOI MARCHE ». L'API valide dans l'ordre
                identifiants → destinataire → reste : une gamme inexistante ou un expéditeur non
                autorisé passeraient cette épreuve et échoueraient au premier vrai SMS (mesuré
                2026-09-01). Le mot doit rester en deçà de ce qu'on sait (§23.0). */}
            <b>Envoi de SMS</b> — {p.envoi.ok
              ? <>identifiants <b>acceptés</b>{p.envoi.account && <> pour {p.envoi.account}</>} — l’expéditeur et la gamme, eux, ne se vérifient qu’à l’envoi.</>
              : <>refusé : {p.envoi.error ?? 'motif non rendu'}</>}
          </div>
          <div style={{ color: p.revente.ok ? 'var(--ok-fg)' : 'var(--da-fg)' }}>
            <b>Sous-comptes et crédits</b> — {p.revente.ok
              ? <>
                  accepté{p.revente.mode && <> (par {p.revente.mode})</>}
                  {p.revente.subaccounts !== undefined && <> · <b>{p.revente.subaccounts}</b> sous-compte(s)</>}
                  {p.revente.money !== undefined && <> · réserve <b>{p.revente.money}</b></>}
                </>
              : <>refusé : {p.revente.error ?? 'motif non rendu'}</>}
            {/* Un compte VALIDE mais ordinaire : l'authentification passe, la liste est refusée. */}
            {p.revente.subaccountsError && <div>⚠️ Ce n’est pas un compte maître : {p.revente.subaccountsError}</div>}
          </div>
          {/* ⚠️ LA MOITIÉ INTROUVABLE : l'interface du fournisseur la nomme « user id », pas « user
              key » — un opérateur qui cherche une clé ne la voit pas. Affichée ici, elle permet de
              basculer vers le jeton et de supprimer le dernier mot de passe stocké. */}
          {p.revente.mode === 'identifiants' && p.revente.userKey && (
            <div style={{ color: 'var(--ink700)' }}>
              Clé utilisateur : <b className="mono">{p.revente.userKey}</b> — reportez-la dans « Jeton d’API »
              pour n’avoir plus aucun mot de passe enregistré.
            </div>
          )}
        </div>
      )}

      <div style={groupHeading}>
        <Icon name="outbound" size={14} />
        <b style={{ color: 'var(--ink700)' }}>Envoi</b>
        <span style={{ color: 'var(--ink500)' }}>API HTTP v2 — e-mail et clé, sans mot de passe</span>
      </div>
      {dEnvoi.map((s) => <LigneReglage key={s.key} s={s} onSave={(v) => onWrite(s.key, v)} />)}

      <div style={groupHeading}>
        <Icon name="users" size={14} />
        <b style={{ color: 'var(--ink700)' }}>Sous-comptes et crédits</b>
        <span style={{ color: 'var(--ink500)' }}>API REST v1.0 — jeton, ou compte et mot de passe</span>
      </div>
      {dRevente.map((s) => <LigneReglage key={s.key} s={s} onSave={(v) => onWrite(s.key, v)} />)}

      {repli.length > 0 && (
        <>
          {/* ⚠️ OUVERT D'OFFICE QUAND C'EST LUI QUI SERT : replier la voie réellement utilisée
              cacherait précisément ce qu'il faut corriger le jour où elle est refusée. */}
          <button
            type="button"
            onClick={() => setReplieOuvert((v) => !v)}
            style={{
              width: '100%', textAlign: 'left', border: 0, cursor: 'pointer',
              borderTop: '1px solid var(--ink200)', background: 'transparent', color: 'var(--ink500)',
              padding: '11px 18px', fontSize: 'var(--t-note)', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <Icon name={replieOuvert ? 'chevronD' : 'chevronR'} size={14} />
            <b style={{ color: 'var(--ink700)' }}>Repli REST — compte et mot de passe</b>
            <span>{aJeton ? 'utilisé seulement si le jeton est retiré' : 'actuellement le seul accès REST'}</span>
          </button>
          {replieOuvert && repli.map((s) => <LigneReglage key={s.key} s={s} onSave={(v) => onWrite(s.key, v)} />)}
        </>
      )}
    </CarteIntegration>
  );
}

// ── ESENDEX ───────────────────────────────────────────────────────────────────────────────────────

/**
 * L'intégration **Esendex** — la plateforme SMS depuis le 2026-09-01.
 *
 * ⚠️ UNE SEULE CAPACITÉ, ET C'EST LE PROGRÈS À MONTRER. SMS Envoi demandait deux jeux d'identifiants
 * pour deux capacités disjointes (envoyer / revendre) ; ici la même clé ouvre l'envoi, le solde et la
 * délégation aux sous-comptes. La carte n'a donc plus deux blocs à afficher.
 *
 * ⚠️ L'AVERTISSEMENT « pas encore utilisé » NE DÉGRADE PAS L'ÉTAT ici, contrairement au portefeuille :
 * le compte SERT déjà — l'épreuve de connexion l'utilise. Ce qui n'est pas branché, c'est l'ENVOI, et
 * c'est le bandeau qui le dit. Rétrograder la pastille en « inerte » ferait croire qu'aucune
 * vérification n'est possible, alors que le bouton fonctionne.
 */
export function CarteEsendex({ settings, onWrite }: {
  settings: BoPlatformSetting[];
  onWrite: (key: string, v: { value?: string | null; secret?: string }) => Promise<void>;
}) {
  const [encours, setEncours] = useState(false);
  const [res, setRes] = useState<Awaited<ReturnType<typeof api.testBoEsendex>> | null>(null);
  const [teste, setTeste] = useState<Date | null>(null);

  const compte = settings.find((s) => s.key === 'esendex.account');
  const aReference = Boolean(compte?.value);
  const aCle = Boolean(compte?.secretSetAt);
  // Les mentions « pas encore branché » sont fusionnées : répétées par ligne, elles ne se lisent plus.
  const inertes = [...new Set(settings.map((s) => s.notWired).filter(Boolean))] as string[];

  const etat: EtatIntegration = useMemo(() => {
    const p = res?.probe;
    if (p?.ok) return { k: 'eprouve', a: heure(teste ?? new Date()) };
    if (res && !res.ok) return { k: 'echec', motif: p?.error ?? res.error ?? 'motif non rendu' };
    // ⚠️ Les deux moitiés vont ENSEMBLE : la référence identifie le compte, la clé l'ouvre. Nommer
    //    celle qui manque évite d'ouvrir chaque champ pour la trouver.
    if (aReference && aCle) return { k: 'configure' };
    if (!aReference && !aCle) return { k: 'absent' };
    return { k: 'incomplet', manque: aReference ? 'la clé d’API' : 'la référence de compte (EXFR…)' };
  }, [res, teste, aReference, aCle]);

  const lancer = async () => {
    setEncours(true); setRes(null);
    try { setRes(await api.testBoEsendex()); setTeste(new Date()); }
    catch (e) {
      setRes({ ok: false, error: e instanceof Error ? e.message : String(e) });
      setTeste(new Date());
    } finally { setEncours(false); }
  };

  const p = res?.probe;
  return (
    <CarteIntegration
      icone="phone" heading="SMS" fournisseur="Esendex" etat={etat}
      stockage="Base interne du back-office — jamais dans une base magasin."
      role={<>
        Notre compte Esendex : <b>une seule clé</b> ouvre l’envoi des SMS, la lecture des soldes et la
        délégation de crédits aux sous-comptes des magasins. Sans lui, aucun SMS ne part.
      </>}
      avertissement={inertes.length ? inertes.join(' ') : undefined}
      action={
        <button type="button" className="btn ghost" onClick={() => void lancer()} disabled={encours}>
          {encours ? 'Épreuve…' : 'Éprouver le compte'}
        </button>
      }
    >
      {/* ⚠️ RÉPONDU SANS SONDE = ANOMALIE, pas un état neutre : sans cette ligne, la carte retombe en
          silence sur « configuré » et l'opérateur croit que son clic n'a rien donné. */}
      {res && res.ok && !res.probe && (
        <div style={{ padding: '12px 18px', background: 'var(--wa-bg)', color: 'var(--wa-fg)', fontSize: 'var(--t-note)', lineHeight: 1.55 }}>
          <b>Réponse inattendue</b> : le back-office a répondu sans rendre le résultat de l’épreuve.
        </div>
      )}
      {res && (res.probe || !res.ok) && (
        <div style={{
          padding: '12px 18px', fontSize: 'var(--t-note)', lineHeight: 1.55,
          background: res.ok ? 'var(--ok-bg)' : 'var(--da-bg)', color: res.ok ? 'var(--ok-fg)' : 'var(--da-fg)',
        }}>
          {res.ok ? (
            <>
              <b>Compte joint</b>{p?.accountReference && <> — {p.accountReference}</>}
              {/* ⚠️ LE SOLDE EST AFFICHÉ TEL QUEL, jamais arrondi : Esendex rend cinq décimales, et le
                  reformater ici ferait diverger notre affichage du leur. */}
              {p?.available !== undefined && <> · solde <b className="mono">{p.available}</b></>}
              {p?.reserved !== undefined && <> (réservé <span className="mono">{p.reserved}</span>)</>}
              {/* ⚠️ ABSENCE DE SOLDE ≠ SOLDE NUL : leur doc le réserve aux comptes prépayés. */}
              {p?.available === undefined && <> — aucun solde rendu (compte post-payé, selon leur documentation).</>}
            </>
          ) : (
            <><b>Échec</b> : {p?.error ?? res.error ?? 'motif non rendu'}</>
          )}
        </div>
      )}

      {settings.map((s) => <LigneReglage key={s.key} s={s} onSave={(v) => onWrite(s.key, v)} />)}
    </CarteIntegration>
  );
}

// ── WALLET ────────────────────────────────────────────────────────────────────────────────────────

/**
 * L'intégration wallet — seulement ce qui est GÉNÉRAL. Les identifiants du commerçant vivent dans SA
 * base, et l'activation du pass tout au bout, chez le client final.
 * ⚠️ AUCUN BOUTON D'ÉPREUVE : rien n'appelle encore Eldowallet depuis la Console. En offrir un serait
 *    une option qui n'existe pas (§23.0) — l'absence de bouton est l'information.
 */
export function CarteWallet({ settings, onWrite }: {
  settings: BoPlatformSetting[];
  onWrite: (key: string, v: { value?: string | null; secret?: string }) => Promise<void>;
}) {
  const url = settings.find((s) => s.key === 'wallet.api_url');
  // Les mentions « pas encore branché » des lignes sont FUSIONNÉES ici : répétées, elles ne se lisent plus.
  const inertes = [...new Set(settings.map((s) => s.notWired).filter(Boolean))] as string[];
  // ⚠️ « INERTE » L'EMPORTE SUR « CONFIGURÉ » : tant que rien ne consomme ces valeurs, les annoncer
  //    configurées ferait croire à un canal branché — le défaut §23.0 que la carte dénonce plus bas.
  // Mémoïsé pour la même raison que la carte SMS : il part en dépendance de l'effet de rapport.
  const etat: EtatIntegration = useMemo(
    () => (inertes.length ? { k: 'inerte' } : url?.value ? { k: 'configure' } : { k: 'absent' }),
    [inertes.length, url?.value],
  );

  return (
    <CarteIntegration
      icone="wallet" heading="Portefeuille" fournisseur="Eldowallet" etat={etat}
      stockage="Base interne du back-office — les identifiants du commerçant, eux, vivent dans SA base."
      role={<>
        L'adresse de l'API et la langue de ses réponses, <b>identiques pour tous les magasins</b>. Ce qui
        est propre à un commerçant (ses identifiants, son journal) n'entre jamais ici.
      </>}
      avertissement={inertes.length ? inertes.join(' ') : undefined}
    >
      {settings.map((s) => <LigneReglage key={s.key} s={s} onSave={(v) => onWrite(s.key, v)} />)}
    </CarteIntegration>
  );
}

// ── LE BANDEAU DE SYNTHÈSE ────────────────────────────────────────────────────────────────────────

/**
 * Ce qu'on veut savoir en arrivant : combien d'intégrations attendent quelque chose.
 * ⚠️ COMPTE MESURÉ sur les cartes rendues, jamais un chiffre écrit à la main — un bandeau qui annonce
 * « tout est configuré » pendant qu'une carte est vide serait le pire endroit où mentir.
 */
export function SyntheseIntegrations({ etats }: { etats: EtatIntegration[] }) {
  const n = (k: EtatIntegration['k']) => etats.filter((e) => e.k === k).length;
  const groupes: Array<{ k: EtatIntegration['k']; mot: string }> = [
    { k: 'echec', mot: 'en échec' },
    { k: 'incomplet', mot: 'incomplète(s)' },
    { k: 'absent', mot: 'non configurée(s)' },
    { k: 'inerte', mot: 'inerte(s)' },
    { k: 'configure', mot: 'configurée(s)' },
    { k: 'eprouve', mot: 'éprouvée(s)' },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
      {groupes.filter((g) => n(g.k) > 0).map((g) => (
        <Pill key={g.k} tone={ETAT_RENDU[g.k].tone} shape="tight">{n(g.k)} {g.mot}</Pill>
      ))}
      {/* ⚠️ DIT, sans quoi un badge vert au rechargement affirmerait un fait non revérifié. */}
      <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
        Les épreuves ne sont pas conservées : elles valent pour cette session.
      </span>
    </div>
  );
}
